import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath, normalizeSoccerRedirect, accountPath } from '../utils/paths';
import { normalizeClubSport } from '../config/clubSports';
import './PlayerRegistration.css';
import './AccountProfile.css';

const PlayerRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [account, setAccount] = useState(null);
  const [formData, setFormData] = useState({
    position: 'MIDFIELDER',
    skillLevel: 5,
    skillCategory: '',
  });
  const [requestClubId, setRequestClubId] = useState('');
  const [approvedClubs, setApprovedClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      const clubId = searchParams.get('clubId') || sessionStorage.getItem('votingClubId');
      const registerPath = clubId
        ? `/register?clubId=${encodeURIComponent(clubId)}`
        : '/register';
      navigate(registerPath, { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  useEffect(() => {
    if (!user?.id) return;
    api.getAccountProfile(user.id).then(setAccount).catch(() => setAccount(null));
  }, [user?.id]);

  useEffect(() => {
    const preselectedClub = searchParams.get('clubId') || sessionStorage.getItem('votingClubId');
    if (preselectedClub) setRequestClubId(preselectedClub);
  }, [searchParams]);

  useEffect(() => {
    const loadClubs = async () => {
      try {
        const data = await api.getApprovedClubs();
        setApprovedClubs(
          (Array.isArray(data) ? data : []).filter(
            (club) => normalizeClubSport(club.sport) === 'SOCCER'
          )
        );
      } catch {
        setApprovedClubs([]);
      }
    };
    loadClubs();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'skillLevel' ? parseInt(value, 10) : value,
    }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        name: account?.name || user.name,
        email: user.email,
        phoneNumber: account?.phoneNumber || null,
        dateOfBirth: account?.dateOfBirth || null,
        position: formData.position,
        skillLevel: formData.skillLevel,
        skillCategory: formData.skillCategory || null,
        ...(account?.profilePictureUrl && { profilePictureUrl: account.profilePictureUrl }),
      };
      const response = await api.registerPlayer(payload);
      let successMessage = `Soccer player profile saved for ${response.name}.`;

      const clubIdToJoin = requestClubId || sessionStorage.getItem('votingClubId');
      if (clubIdToJoin && response.id) {
        try {
          await api.requestClubMembership(clubIdToJoin, response.id);
          successMessage += ' You have requested to join the club. Waiting for club admin approval.';
        } catch (clubErr) {
          if (clubErr.message?.includes('already') || clubErr.message?.includes('pending')) {
            successMessage += ` ${clubErr.message}`;
          }
        }
      }

      setSuccess(successMessage);
      const redirectUrl = sessionStorage.getItem('votingRedirectUrl');
      setTimeout(() => {
        if (redirectUrl) {
          sessionStorage.removeItem('votingRedirectUrl');
          sessionStorage.removeItem('votingClubId');
          navigate(normalizeSoccerRedirect(redirectUrl), { replace: true });
        } else {
          navigate(soccerPath('/players/profile'));
        }
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to register player');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <h2>Complete your soccer player profile</h2>
      <p className="form-help" style={{ marginBottom: '1rem' }}>
        Add soccer-specific details here so you can join clubs, vote on practices, and appear on
        team lineups. Your name, photo, and contact info live on your{' '}
        <Link to={accountPath()}>account profile</Link>.
      </p>

      {account && (
        <p className="account-profile-page__shared-note">
          Signed in as <strong>{account.name}</strong>
          {account.phoneNumber ? ` · ${account.phoneNumber}` : ''}.{' '}
          <Link to={accountPath()}>Update account details →</Link>
        </p>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="position">Playing position *</label>
          <select id="position" name="position" value={formData.position} onChange={handleChange} required>
            <option value="GOALKEEPER">Goalkeeper</option>
            <option value="DEFENDER">Defender</option>
            <option value="MIDFIELDER">Midfielder</option>
            <option value="FORWARD">Forward</option>
            <option value="WINGER">Winger</option>
            <option value="FLEXIBLE">Flexible (multiple positions)</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="skillLevel">Skill level * (1–10): {formData.skillLevel}</label>
          <input
            type="range"
            id="skillLevel"
            name="skillLevel"
            min="1"
            max="10"
            value={formData.skillLevel}
            onChange={handleChange}
            required
          />
          <div className="skill-labels">
            <span>1 (Beginner)</span>
            <span>10 (Expert)</span>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="skillCategory">Experience level (optional)</label>
          <select
            id="skillCategory"
            name="skillCategory"
            value={formData.skillCategory}
            onChange={handleChange}
            style={{ width: '100%', padding: '0.5rem' }}
          >
            <option value="">— Select —</option>
            <option value="BEGINNER">Beginner</option>
            <option value="INTERMEDIATE">Intermediate</option>
            <option value="EXPERT">Expert</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="requestClubId">Request to join a soccer club (optional)</label>
          <select
            id="requestClubId"
            value={requestClubId}
            onChange={(e) => setRequestClubId(e.target.value)}
            style={{ width: '100%', padding: '0.5rem' }}
          >
            <option value="">— No club / I'll join later —</option>
            {approvedClubs
              .filter((club) => club.allowNewMembers !== false)
              .map((club) => (
                <option key={club.id} value={club.id}>
                  {club.name}
                </option>
              ))}
          </select>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving...' : 'Save soccer profile'}
        </button>
      </form>
    </div>
  );
};

export default PlayerRegistration;
